import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Input, Button, Select, Spin } from "antd";
import { Plus, Trash2 } from "feather-icons-react";
import { ROLL_CALL_API_BASE_URL } from "../RollCallConfig/RollCallConfig";
import dayjs from "dayjs";

const { Option } = Select;

const DynamicKPIForm = ({ record, selectedDate, onCancel, onSave }) => {
  const [metricOptions, setMetricOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [entries, setEntries] = useState([]);

  // Fetch Options + Existing Data (Filtered by Date)
  useEffect(() => {
    if (!record?.MetricsId || !selectedDate) return;

    const fetchData = async () => {
      setLoading(true);
      const superId = sessionStorage.getItem("superId");
      const token = sessionStorage.getItem("authToken");

      try {
        const optRes = await fetch(
          `${ROLL_CALL_API_BASE_URL}portal/kpi/getKpiMetricDetails?SuperId=${superId}&MetricId=${record.MetricsId}&Status=1`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const optData = await optRes.json();
        if (optData.status) setMetricOptions(optData.ResultData || []);

        const existRes = await fetch(
          `${ROLL_CALL_API_BASE_URL}portal/kpi/getKpiResponseDetails?SuperId=${superId}&MetricId=${record.MetricsId}&BranchId=0&fromdate=${selectedDate}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const existData = await existRes.json();

        if (existData.status && existData.ResultData?.length > 0) {
          const filtered = existData.ResultData.filter(
            (item) =>
              dayjs(item.DateOfTransaction).format("YYYY-MM-DD") ===
              selectedDate,
          ).map((item) => ({
            id: item.Id,
            MetricDetailId: item.DetailsId,
            Value: item.Value || "",
            HasText: item.HasText,
            HasValue: item.HasValue,
            HasFlag: item.HasFlag,
            MetricTitle: item.MetricTitle,
          }));

          setEntries(
            filtered.length > 0
              ? filtered
              : [
                  {
                    id: Date.now(),
                    MetricDetailId: null,
                    Value: "",
                    HasText: false,
                    HasValue: false,
                    HasFlag: false,
                  },
                ],
          );
        } else {
          setEntries([
            {
              id: Date.now(),
              MetricDetailId: null,
              Value: "",
              HasText: false,
              HasValue: false,
              HasFlag: false,
            },
          ]);
        }
      } catch (err) {
        console.error(err);
        setEntries([
          {
            id: Date.now(),
            MetricDetailId: null,
            Value: "",
            HasText: false,
            HasValue: false,
            HasFlag: false,
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [record?.MetricsId, selectedDate]);

  const addNewRow = () => {
    setEntries([
      ...entries,
      {
        id: Date.now(),
        MetricDetailId: null,
        Value: "",
        HasText: false,
        HasValue: false,
        HasFlag: false,
      },
    ]);
  };

  const removeRow = (id) => {
    if (entries.length === 1) return;
    setEntries(entries.filter((item) => item.id !== id));
  };

  const handleSelectChange = (id, selectedId) => {
    const selected = metricOptions.find((opt) => opt.Id === selectedId);
    setEntries(
      entries.map((item) =>
        item.id === id
          ? {
              ...item,
              MetricDetailId: selectedId,
              HasText: selected?.HasText || false,
              HasValue: selected?.HasValue || false,
              HasFlag: selected?.HasFlag || false,
              Value: "",
            }
          : item,
      ),
    );
  };

  const handleValueChange = (id, value) => {
    setEntries(
      entries.map((item) =>
        item.id === id ? { ...item, Value: value } : item,
      ),
    );
  };

  const handleSubmit = () => {
    onSave(entries);
  };

  return (
    <div className="p-4 border rounded bg-light shadow-sm">
      <h6 className="mb-4 pb-2 border-bottom">
        <i className="fa-solid fa-plus-circle me-2 text-success"></i>
        Update Details for <strong>{record?.MetricName || "KPI"}</strong>
      </h6>

      {loading ? (
        <div className="text-center py-5">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {entries.map((entry) => (
            <div
              key={entry.id}
              className="row g-3 mb-4 p-3 border rounded bg-white"
            >
              <div className="col-md-5 mt-0">
                <label className="form-label">
                  Select Type <span className="text-danger">*</span>
                </label>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select Item"
                  value={entry.MetricDetailId}
                  onChange={(val) => handleSelectChange(entry.id, val)}
                >
                  {metricOptions.map((opt) => (
                    <Option key={opt.Id} value={opt.Id}>
                      {opt.Title}
                    </Option>
                  ))}
                </Select>
              </div>

              <div className="col-md-5 mt-0">
                <label className="form-label">
                  {entry.HasText
                    ? "Text Value"
                    : entry.HasValue
                      ? "Amount"
                      : "Value"}
                  <span className="text-danger">*</span>
                </label>

                {entry.HasText && (
                  <Input
                    placeholder="Enter text"
                    value={entry.Value}
                    onChange={(e) =>
                      handleValueChange(entry.id, e.target.value)
                    }
                  />
                )}

                {entry.HasValue && (
                  <Input
                    type="number"
                    placeholder="Enter amount"
                    value={entry.Value}
                    onChange={(e) =>
                      handleValueChange(entry.id, e.target.value)
                    }
                  />
                )}

                {entry.HasFlag && (
                  <div className="form-check mt-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={!!entry.Value}
                      onChange={(e) =>
                        handleValueChange(entry.id, e.target.checked ? 1 : 0)
                      }
                    />
                    <label className="form-check-label">Yes</label>
                  </div>
                )}
              </div>

              <div className="col-md-2 d-flex align-items-end pb-1 d-none">
                {entries.length > 1 && (
                  <Button
                    danger
                    type="text"
                    icon={<Trash2 size={18} />}
                    onClick={() => removeRow(entry.id)}
                  />
                )}
              </div>
            </div>
          ))}
          <div className="col-4">
            <Button
              type="dashed"
              onClick={addNewRow}
              icon={<Plus size={18} />}
              block
              className="mb-4"
            >
              Add Another Row
            </Button>
          </div>

          <div className="d-flex justify-content-end gap-2">
            <button className="btn btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button className="btn btn-success" onClick={handleSubmit}>
              Save All
            </button>
          </div>
        </>
      )}
    </div>
  );
};

DynamicKPIForm.propTypes = {
  record: PropTypes.object,
  selectedDate: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default DynamicKPIForm;
